import { Tooltip } from "@/components/ui/tooltip"
import { Box, Flex, Heading, IconButton } from "@chakra-ui/react"
import {
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useRef } from "react"
import { FiEdit2, FiEye, FiTrash } from "react-icons/fi"
import { HtmlContent } from "../Common/HtmlContent"
import type { SelectedQuestion } from "./quiz-builder-types"

interface VirtualizedQuestionListProps {
  questions: SelectedQuestion[]
  sectionId: string
  droppableId: string
  onRemoveQuestion: (questionId: string, sectionId?: string) => void
  onPreviewQuestion: (questionId: string) => void
  startIndex?: number
}

/**
 * Virtualized question list for performance optimization with large question sets (50+ questions).
 * Uses @tanstack/react-virtual for efficient rendering of only visible items.
 * Maintains drag-and-drop functionality from @hello-pangea/dnd.
 */
export function VirtualizedQuestionList({
  questions,
  sectionId,
  droppableId,
  onRemoveQuestion,
  onPreviewQuestion,
  startIndex = 0,
}: VirtualizedQuestionListProps) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: questions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120, // Estimated height per question card in pixels
    overscan: 5, // Render 5 extra items above/below viewport for smooth scrolling
  })

  return (
    <Droppable droppableId={droppableId} type="QUESTION">
      {(dropProvided: DroppableProvided) => (
        <Box
          ref={(node: HTMLDivElement | null) => {
            dropProvided.innerRef(node)
            if (parentRef.current !== node) {
              ;(
                parentRef as React.MutableRefObject<HTMLDivElement | null>
              ).current = node
            }
          }}
          {...dropProvided.droppableProps}
          data-testid="virtualized-list"
          maxH="600px"
          overflowY="auto"
          position="relative"
        >
          {/* Virtual container with dynamic height */}
          <Box
            position="relative"
            h={`${virtualizer.getTotalSize()}px`}
            minH="50px"
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const question = questions[virtualItem.index]
              if (!question) return null

              return (
                <Draggable
                  key={question.id}
                  draggableId={`question-${question.id}`}
                  index={virtualItem.index}
                >
                  {(
                    dragProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot,
                  ) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      transform={`translateY(${virtualItem.start}px)`}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      bg={snapshot.isDragging ? "blue.50" : "white"}
                      _dark={{
                        bg: snapshot.isDragging ? "blue.900" : "gray.900",
                      }}
                    >
                      <Flex justify="space-between" align="flex-start" gap={3}>
                        <Box flex="1">
                          <Heading size="sm" mb={2}>
                            Question {startIndex + virtualItem.index + 1}
                          </Heading>
                          <HtmlContent
                            lineClamp={3}
                            content={question.data.question_text}
                            css={{
                              "& p": { margin: 0 },
                            }}
                          />
                        </Box>
                        <Flex gap={2} wrap="wrap">
                          <Tooltip content="Preview question">
                            <IconButton
                              aria-label="Preview question"
                              variant="ghost"
                              size="sm"
                              colorPalette="gray"
                              onClick={() => onPreviewQuestion(question.id)}
                            >
                              <FiEye />
                            </IconButton>
                          </Tooltip>
                          <Tooltip content="Edit question">
                            <IconButton
                              asChild
                              aria-label="Edit question"
                              variant="ghost"
                              size="sm"
                              colorPalette="gray"
                            >
                              <a
                                href={`/questions/${question.id}/edit`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FiEdit2 />
                              </a>
                            </IconButton>
                          </Tooltip>
                          <Tooltip content="Remove question">
                            <IconButton
                              aria-label="Remove question"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() =>
                                onRemoveQuestion(question.id, sectionId)
                              }
                              size="sm"
                            >
                              <FiTrash />
                            </IconButton>
                          </Tooltip>
                        </Flex>
                      </Flex>
                    </Box>
                  )}
                </Draggable>
              )
            })}
          </Box>
          {dropProvided.placeholder}
        </Box>
      )}
    </Droppable>
  )
}
